import React from "react";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";

import {
  FormInput,
  FormMoneyInput,
  InputDate,
} from "../../../../components/Input";
import { ResponsiveFormSelect } from "../ResponsiveFormSelect";
import { PAYMENT_FREQUENCY_OPTIONS, START_DATE_RULES } from "../../constants";
import { LINKED_DATES_HINT, RECOMPUTE_NOTE } from "./constants";
import { EditLiabilityFieldsProps } from "./types";
import {
  Field,
  FieldLabel,
  FormCard,
  LabelRow,
  TwoColumnRow,
} from "../../../../components/FormPanel";
import { PanelNote, ResponsiveRow, UnitField, UnitSuffix } from "../styles";

export const EditLiabilityFields: React.FC<EditLiabilityFieldsProps> = ({
  control,
  isSubmitting,
  isCompact,
  liabilityTypes,
}) => {
  // Only a changed start is sent, so a legacy future start must not block unrelated edits.
  const startDateRules = {
    ...START_DATE_RULES,
    validate: {
      notInFuture: (value: Date | null) =>
        control.getFieldState("startDate").isDirty
          ? START_DATE_RULES.validate.notInFuture(value)
          : true,
    },
  };

  return (
    <>
      <FormCard>
        <Field>
          <LabelRow>
            <FieldLabel htmlFor="edit-liability-name">Loan Name *</FieldLabel>
          </LabelRow>
          <FormInput
            id="edit-liability-name"
            required
            rules={{
              required: "Name is required!",
              minLength: {
                message: "Name should have more than 3 letters!",
                value: 3,
              },
            }}
            name="name"
            placeholder="e.g. Home Loan"
            control={control}
            handleClear={true}
            disabled={isSubmitting}
            autoFocus={true}
          />
        </Field>

        <ResponsiveRow>
          <Field>
            <LabelRow>
              <FieldLabel htmlFor="edit-liability-institution">
                Institution
              </FieldLabel>
            </LabelRow>
            <FormInput
              id="edit-liability-institution"
              name="institution"
              control={control}
              placeholder="e.g. Institution X"
              handleClear={false}
              disabled={isSubmitting}
            />
          </Field>
          <Field>
            <LabelRow>
              <FieldLabel htmlFor="liability-type-select">
                Liability Type *
              </FieldLabel>
            </LabelRow>
            <ResponsiveFormSelect
              isCompact={isCompact}
              testId="liability-type-select"
              rules={{ required: "Liability Type is required!" }}
              name="liabilityType"
              control={control}
              options={liabilityTypes}
              sheetTitle="Select liability type"
              disabled={isSubmitting}
            />
          </Field>
        </ResponsiveRow>
      </FormCard>

      <FormCard>
        <Field>
          <LabelRow>
            <FieldLabel htmlFor="edit-liability-amount">
              Amount Borrowed (€) *
            </FieldLabel>
          </LabelRow>
          <FormMoneyInput
            id="edit-liability-amount"
            required
            rules={{ required: "Amount Borrowed is required!" }}
            name="amount"
            placeholder="€ 0.00"
            control={control}
            handleClear={true}
            disabled={isSubmitting}
          />
        </Field>

        <TwoColumnRow>
          <Field>
            <LabelRow>
              <FieldLabel htmlFor="edit-liability-start-date">
                Start Date *
              </FieldLabel>
            </LabelRow>
            <InputDate
              id="edit-liability-start-date"
              rules={startDateRules}
              name="startDate"
              placeholder="YYYY/MM/DD"
              control={control}
              maxDate={new Date()}
              disabled={isSubmitting}
            />
          </Field>
          <Field>
            <LabelRow>
              <FieldLabel htmlFor="edit-liability-term">
                Term (Years) *
              </FieldLabel>
            </LabelRow>
            <UnitField>
              <FormInput
                id="edit-liability-term"
                required
                rules={{
                  required: "Term is required!",
                  min: { message: "Term must be at least 1 year", value: 1 },
                  max: {
                    message: "Term must be 100 years or less",
                    value: 100,
                  },
                }}
                name="term"
                type="number"
                placeholder="e.g. 40"
                control={control}
                handleClear={false}
                disabled={isSubmitting}
              />
              <UnitSuffix>yrs</UnitSuffix>
            </UnitField>
          </Field>
        </TwoColumnRow>

        <Field>
          <LabelRow>
            <FieldLabel htmlFor="edit-liability-end-date">
              End Date *
            </FieldLabel>
          </LabelRow>
          <InputDate
            id="edit-liability-end-date"
            rules={{ required: "End date is required!" }}
            name="endDate"
            placeholder="YYYY/MM/DD"
            control={control}
            disabled={isSubmitting}
          />
        </Field>

        <PanelNote $tone="muted">
          <ArrowForwardIcon />
          {LINKED_DATES_HINT}
        </PanelNote>
      </FormCard>

      <FormCard>
        <TwoColumnRow>
          <Field>
            <LabelRow>
              <FieldLabel htmlFor="payment-frequency-select">
                Payment Frequency
              </FieldLabel>
            </LabelRow>
            <ResponsiveFormSelect
              isCompact={isCompact}
              testId="payment-frequency-select"
              required={false}
              name="paymentFrequency"
              control={control}
              options={PAYMENT_FREQUENCY_OPTIONS}
              sheetTitle="Select payment frequency"
              disabled={isSubmitting}
            />
          </Field>
          <Field>
            <LabelRow>
              <FieldLabel htmlFor="edit-liability-payment-due-day">
                Payment Due Day
              </FieldLabel>
            </LabelRow>
            <FormInput
              id="edit-liability-payment-due-day"
              rules={{
                min: {
                  message: "Payment Due Day must be between 1 and 31",
                  value: 1,
                },
                max: {
                  message: "Payment Due Day must be between 1 and 31",
                  value: 31,
                },
              }}
              name="paymentDueDay"
              type="number"
              placeholder="e.g. 1"
              control={control}
              handleClear={false}
              disabled={isSubmitting}
            />
          </Field>
        </TwoColumnRow>
      </FormCard>

      <PanelNote $tone="warning">
        <InfoOutlinedIcon />
        {RECOMPUTE_NOTE}
      </PanelNote>
    </>
  );
};
